/**
 * How a shape sits in the 3D world.
 *
 * The flat map has no equivalent — everything there is on the ground by
 * definition — so this only affects the 3D view, and every shape falls back to
 * the project default until someone sets it deliberately.
 */
export enum AltitudeMode3d {
  /** Follow the project's default. Only valid on a shape, never as a default. */
  DEFAULT = 'default',
  /**
   * Lie on the bare earth, ignoring anything built on it.
   *
   * The right answer for site work: a boundary or an excavation is measured on
   * the ground, and draping it over the buildings standing there would be
   * wrong as well as ugly.
   */
  GROUND = 'ground',
  /**
   * Lie on the surface, buildings included.
   *
   * The shape follows the mesh, so it hugs a roof's slopes, parapets and
   * plant. Good for seeing coverage; less good for a quote, because a draped
   * shape reads as bumpy rather than as a plane.
   */
  MESH = 'mesh',
  /**
   * Sit flat at a fixed height above the ground -- no longer offered (user,
   * 2026-10-25): it was GROUND raised by a set height, which a shape's Raise
   * or lower already is. Kept so saved shapes and .mmp files that carry it
   * can be read; they are read as GROUND with that height
   * (GeometryConverter's fromFixedHeight).
   */
  FIXED = 'fixed',
  /**
   * Stand where it was put: each corner at the height it was measured at,
   * whatever is under it now.
   *
   * A shape moved or turned on the 3D view's gizmo goes as one piece. Draped
   * on the surface instead, a roof nudged past its edge ran down the wall to
   * the street at that corner, while its material, laid through the
   * corners' measured heights, stayed up on the roof. Only on a shape, set
   * by the gizmo (or chosen back from the others); never a project default.
   */
  PLACED = 'placed',
}

/** The choices offered as a project default — DEFAULT would be circular. */
export const DEFAULT_ALTITUDE_MODES = [
  AltitudeMode3d.MESH,
  AltitudeMode3d.GROUND,
];
